import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SectionHead, Reveal, Crosshair } from "./Reveal";
import { useLang } from "../../i18n/LanguageContext";

export const Products = () => {
  const { t } = useLang();
  const [active, setActive] = useState(0);
  const item = t.products.items[active];

  return (
    <section id="solutions" className="relative bg-sand py-24 sm:py-32 border-y border-technical" data-testid="products-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <SectionHead eyebrow={t.products.eyebrow} title={t.products.title} desc={t.products.desc} testid="products" />

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 flex flex-col">
            {t.products.items.map((p, i) => (
              <button
                key={p.key}
                data-testid={`product-tab-${p.key}`}
                onClick={() => setActive(i)}
                className={`group relative flex items-center gap-4 border-b border-technical px-2 py-5 text-left transition-colors duration-200 ${
                  i === active ? "text-red-brand" : "text-ink hover:text-red-brand"
                }`}
              >
                <span className={`font-mono-tech text-[10px] tracking-[0.15em] ${i === active ? "text-red-brand" : "text-[#9ca3af]"}`}>
                  {String(i + 1).padStart(2, "0")} / {p.code}
                </span>
                <span className="font-display text-base sm:text-lg font-bold tracking-tight leading-snug">{p.name}</span>
                {i === active && (
                  <motion.span layoutId="tab-marker" className="absolute left-0 top-0 h-full w-[3px] bg-red-brand" />
                )}
              </button>
            ))}
          </div>

          <div className="lg:col-span-8 relative min-h-[420px]">
            <Crosshair className="-top-2 -left-2 z-10" />
            <Crosshair className="-top-2 -right-2 z-10" />
            <AnimatePresence mode="wait">
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="grid grid-cols-1 md:grid-cols-2 border border-technical bg-white"
                data-testid="product-detail"
              >
                <div className="chamfer relative overflow-hidden md:h-full min-h-[260px]">
                  <img src={item.img} alt={item.name} className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#16181b]/70 to-transparent" />
                  <span className="absolute bottom-3 left-3 font-mono-tech text-[10px] tracking-[0.25em] text-[#f8f7f4]/90">
                    {item.code} — SPEC SHEET
                  </span>
                </div>
                <div className="p-7 sm:p-9 flex flex-col justify-between">
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-ink">{item.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#525964]">{item.desc}</p>
                  </div>
                  <div className="mt-8">
                    <p className="eyebrow">{t.products.specLabel}</p>
                    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                      {item.specs.map((s) => (
                        <li key={s} className="flex items-center gap-2.5 font-mono-tech text-[11px] tracking-wide text-[#24282d]">
                          <span className="inline-block h-1.5 w-1.5 bg-red-brand rotate-45" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
